import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import {
  dryRunImportOrgFromEdlinkDistrictEntry,
  getOrgs,
  getUnimportedEdlinkOrgEntries,
  importOrgFromEdlinkDistrictEntry,
} from '@/lib/api-calls';
import { edlinkIdSchema, EdlinkOrgChartDryRunResult, EdlinkOrgChartImportSummary } from '@airreading/common/data';
import * as Sentry from '@sentry/react';

import { exportDryRunToCSV, exportSummaryToCSV } from '@/lib/edlink-csv';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { airServerErrorFrom } from '@/lib/error-handling';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EdlinkIdCode } from '@/components/alphanumeric-code';
import { Button } from '@/components/ui/button';
import { ImportSummary } from './import-summary';

const formSchema = z.object({
  districtId: edlinkIdSchema,
});

type ImportFromEdlinkProps = {
  trigger: React.ReactNode;
};

type EdlinkImportResult =
  | {
      type: 'success';
      summary: EdlinkOrgChartImportSummary | EdlinkOrgChartDryRunResult;
      districtName: string;
      districtId: string;
    }
  | { type: 'error'; message: string };

function EdlinkImportResultView({ result }: { result: EdlinkImportResult | null }) {
  if (!result) {
    return null;
  }

  if (result.type === 'error') {
    return (
      <Alert variant="destructive">
        <AlertDescription>{result.message}</AlertDescription>
      </Alert>
    );
  }

  return <ImportSummary summary={result.summary} districtName={result.districtName} districtId={result.districtId} />;
}

export const ImportFromEdlink = ({ trigger }: ImportFromEdlinkProps) => {
  const { mutateAsync, isPending: isImportPending } = useMutation({
    mutationFn: importOrgFromEdlinkDistrictEntry,
  });

  const { mutateAsync: dryRunMutate, isPending: isDryRunPending } = useMutation({
    mutationFn: dryRunImportOrgFromEdlinkDistrictEntry,
  });

  const { data: viableEdlinkDistrictEntries } = useQuery(getUnimportedEdlinkOrgEntries());
  const [edlinkImport, setEdlinkImport] = useState<EdlinkImportResult | null>(null);
  const [dryRunResult, setDryRunResult] = useState<EdlinkImportResult | null>(null);
  const [confirmPending, setConfirmPending] = useState<z.infer<typeof formSchema> | null>(null);
  const queryClient = useQueryClient();

  const hasSuccess = edlinkImport?.type === 'success' || dryRunResult?.type === 'success';

  const getEntry = (districtId: string) =>
    viableEdlinkDistrictEntries?.find((entry) => entry.districtId === districtId);

  const onSubmit = ({ formData }: SubmitProps<z.infer<typeof formSchema>>) => {
    setConfirmPending(formData);
  };

  const onConfirmImport = async () => {
    if (!confirmPending) {
      return;
    }

    const entry = getEntry(confirmPending.districtId);
    setConfirmPending(null);

    if (!entry) {
      setEdlinkImport({ type: 'error', message: `Selected unknown Edlink district ID: ${confirmPending.districtId}` });
      return;
    }

    try {
      const summary = await mutateAsync(entry);
      setDryRunResult(null);
      setEdlinkImport({ type: 'success', summary, districtName: entry.districtName, districtId: entry.districtId });
      queryClient.invalidateQueries(getOrgs());
      queryClient.invalidateQueries(getUnimportedEdlinkOrgEntries());
    } catch (e: unknown) {
      Sentry.captureException(e, { tags: { feature: 'edlink-import', districtId: entry.districtId } });
      const { userFacingMessage } = airServerErrorFrom(e) || {};
      const errorReason = userFacingMessage ? `Reason: ${userFacingMessage}` : '';

      setEdlinkImport({
        type: 'error',
        message: `Edlink district import failed. Please try again or contact support. ${errorReason}`,
      });
    }
  };

  const onClose = () => {
    setEdlinkImport(null);
    setDryRunResult(null);
  };

  const isActionDisabled = (districtId: string, isValid: boolean) =>
    !isValid ||
    !districtId ||
    !viableEdlinkDistrictEntries?.some((e) => e.districtId === districtId) ||
    isDryRunPending ||
    isImportPending;

  const onDownloadSummary = () => {
    const isEdlinkImportSuccess = edlinkImport?.type === 'success';
    const isDryRunSuccess = dryRunResult?.type === 'success';

    const result = isEdlinkImportSuccess ? edlinkImport : isDryRunSuccess ? dryRunResult : null;

    if (!result) {
      return;
    }

    const { summary, districtName, districtId } = result;

    if (summary instanceof EdlinkOrgChartImportSummary) {
      exportSummaryToCSV(summary, `${districtName} - ${districtId} - Import Summary.csv`);
    } else {
      exportDryRunToCSV(summary, `${districtName} - ${districtId} - Dry Run Summary.csv`);
    }
  };

  const onDryRun = (districtId: string) => {
    const entry = getEntry(districtId);

    if (!entry) {
      return;
    }

    setDryRunResult(null);
    setEdlinkImport(null);

    dryRunMutate(entry)
      .then((summary) =>
        setDryRunResult({ type: 'success', summary, districtName: entry.districtName, districtId: entry.districtId }),
      )
      .catch((e: unknown) => {
        Sentry.captureException(e, { tags: { feature: 'edlink-import-dry-run', districtId: entry.districtId } });
        const { userFacingMessage } = airServerErrorFrom(e) || {};
        const errorReason = userFacingMessage ? `Reason: ${userFacingMessage}` : '';

        setDryRunResult({
          type: 'error',
          message: `Dry run failed. Please try again. ${errorReason}`,
        });
      });
  };

  return (
    <>
      <AlertDialog open={!!confirmPending} onOpenChange={(open) => !open && setConfirmPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{dryRunResult ? 'Confirm Import' : 'Skip test and import?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {dryRunResult
                ? 'This action will import the records based on the current dry run result.'
                : 'You are about to import data without running a test. This might introduce incorrect records.'}{' '}
              Please confirm before proceeding.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmImport}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <FormModal
        modalTitle="Sync Edlink district"
        onSubmit={onSubmit}
        onClose={onClose}
        confirmBtnLabel="Sync"
        cancelBtnLabel="Close modal"
        submitButtonProps={(form) => {
          const districtId = form.watch('districtId');
          const { isValid } = form.formState;
          return {
            loading: isImportPending,
            disabled: isActionDisabled(districtId, isValid),
          };
        }}
        formSchema={formSchema}
        trigger={trigger}
        isFullScreen={hasSuccess}
        preventExternalClose
        footerContent={
          hasSuccess || viableEdlinkDistrictEntries?.length
            ? ({ form }) => {
                const districtId = form.watch('districtId');
                const { isValid } = form.formState;
                return (
                  <>
                    {hasSuccess && (
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={isDryRunPending || isImportPending}
                        onClick={onDownloadSummary}
                      >
                        {edlinkImport?.type === 'success' ? 'Download Summary' : 'Download Dry Run Summary'}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="secondary"
                      loading={isDryRunPending}
                      disabled={isActionDisabled(districtId, isValid)}
                      onClick={() => onDryRun(form.getValues('districtId'))}
                    >
                      Dry Run
                    </Button>
                  </>
                );
              }
            : undefined
        }
      >
        {({ form }) => {
          const { isSubmitting } = form.formState;
          return (
            <>
              <div className="grid gap-4 py-4">
                {(() => {
                  if (viableEdlinkDistrictEntries === null) {
                    return (
                      <Alert variant="destructive">
                        <AlertDescription>
                          Could not connect to Edlink API. Please contact engineering.
                        </AlertDescription>
                      </Alert>
                    );
                  }
                  return null;
                })()}
                {viableEdlinkDistrictEntries ? (
                  <FormSingleSelect
                    label="Edlink ID"
                    options={viableEdlinkDistrictEntries.map((entry) => entry.districtId)}
                    renderOption={(districtId) => {
                      const entry = viableEdlinkDistrictEntries.find((entry) => entry.districtId === districtId);
                      return entry ? (
                        <>
                          {entry.districtName} -{' '}
                          <EdlinkIdCode code={districtId} showCopyButton={false} showColor={false} />
                        </>
                      ) : (
                        districtId
                      );
                    }}
                    form={form}
                    name="districtId"
                    selectProps={{ disabled: isSubmitting || isDryRunPending }}
                  />
                ) : null}
                <EdlinkImportResultView result={dryRunResult} />
                <EdlinkImportResultView result={edlinkImport} />
              </div>
            </>
          );
        }}
      </FormModal>
    </>
  );
};
